import moment from "moment";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";

import SafeECharts from "../../../stats/SafeECharts";

interface MonthlyStatsData {
  month: number;
  paidOutComms: number;
  uncollectedComms: number;
  activeReferrals: number;
}

interface MonthlyStatsGraphProps {
  data?: MonthlyStatsData[];
  year: number | string;
  isLoading?: boolean;
  style?: React.CSSProperties;
  className?: string;
  [key: string]: unknown;
}

const MonthlyStatsGraph = ({
  data = [],
  year,
  isLoading = false,
  style,
  className,
  ...EChartsProps
}: MonthlyStatsGraphProps) => {
  const { t, i18n } = useTranslation("referrer", { keyPrefix: "distributor.monthlyStatsGraph" });

  useEffect(() => {
    moment.locale(i18n.language);
  }, [i18n.language]);

  const { paidOutComms, uncollectedComms, activeReferrals, months } = useMemo(() => {
    // Check if we're displaying the current year
    const currentYear = parseInt(moment().format("YYYY"));
    const isCurrentYear = parseInt(String(year)) === currentYear;

    // For current year, only show months up to current month
    const monthsToShow = isCurrentYear ? parseInt(moment().format("M")) : 12;

    // Initialize arrays for the appropriate number of months with zeros
    const result = {
      paidOutComms: Array(monthsToShow).fill(0),
      uncollectedComms: Array(monthsToShow).fill(0),
      activeReferrals: Array(monthsToShow).fill(0),
      months: Array.from({ length: monthsToShow }, (_, i) => moment().month(i).format("MMM")),
    };

    if (Array.isArray(data) && data.length > 0) {
      // Fill in the data for months that exist in the input
      data.forEach(({ month, paidOutComms, uncollectedComms, activeReferrals }) => {
        if (month >= 1 && month <= monthsToShow) {
          const index = month - 1; // Convert to 0-based index
          result.paidOutComms[index] = paidOutComms || 0;
          result.uncollectedComms[index] = uncollectedComms || 0;
          result.activeReferrals[index] = activeReferrals || 0;
        }
      });
    }

    return result;
  }, [data, year]);

  if (isLoading) return <Skeleton height={style?.height || 400} />;

  const successColor = getComputedStyle(document.documentElement).getPropertyValue("--success").trim();
  const warningColor = getComputedStyle(document.documentElement).getPropertyValue("--warning").trim();
  const infoColor = getComputedStyle(document.documentElement).getPropertyValue("--info").trim();

  return (
    <SafeECharts
      {...EChartsProps}
      className={className}
      style={style}
      option={{
        tooltip: {
          trigger: "axis",
          axisPointer: {
            type: "shadow",
            label: {
              formatter: ({ value }: { value: string }) => moment(value, "MMM").format("MMMM"),
            },
          },
        },
        grid: {
          containLabel: true,
          top: 30,
          right: 50,
          left: 50,
          bottom: 40,
        },
        yAxis: [
          {
            type: "value",
            name: `${t("commissionsLabel")} (€)`,
            nameLocation: "middle",
            nameGap: 50,
            position: "left",
            axisLabel: { formatter: (n: number) => n + " €" },
          },
          {
            type: "value",
            name: t("activeClientsLabel"),
            nameLocation: "middle",
            nameGap: 30,
            position: "right",
            minInterval: 1, // Force only integer values
            axisLine: { show: true, lineStyle: { color: infoColor } },
            axisLabel: { formatter: (n: string) => parseInt(n) },
          },
        ],
        xAxis: {
          type: "category",
          // name: 'Mes',
          nameLocation: "middle",
          nameGap: 25,
          data: months,
        },
        series: [
          {
            name: t("activeClientsLabel"),
            type: "line",
            yAxisIndex: 1,
            symbol: "circle",
            symbolSize: 8,
            color: infoColor,
            lineStyle: {
              width: 3,
              type: "solid",
            },
            areaStyle: {
              color: {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  {
                    offset: 0,
                    color: infoColor + "CC", // Color with 80% opacity
                  },
                  {
                    offset: 1,
                    color: infoColor + "33", // Color with 20% opacity
                  },
                ],
              },
            },
            data: activeReferrals,
            tooltip: { valueFormatter: (n: number) => n },
            z: 1, // Lower z-index to ensure it's drawn behind bars
          },
          {
            name: t("totalPaidOutLabel"),
            type: "bar",
            stack: "commissions",
            color: successColor,
            barWidth: "40%",
            data: paidOutComms,
            tooltip: { valueFormatter: (n: number) => n.toFixed(2) + " €" },
            z: 2, // Higher z-index to place bars in front
          },
          {
            name: t("totalUncollectedLabel"),
            type: "bar",
            stack: "commissions",
            color: warningColor,
            barWidth: "40%",
            data: uncollectedComms,
            tooltip: { valueFormatter: (n: number) => n.toFixed(2) + " €" },
            z: 2, // Same z-index as other bar for consistent rendering
          },
        ],
      }}
    />
  );
};

export default MonthlyStatsGraph;
